// Canonical session gate (2026-10-06) — the ONE place that decides whether this
// browser session may render the firm application shell.
//
// Live sources of truth (replacing the old one-time getUserProfile/getFirm reads):
//   * Firebase Auth state (onAuthChange);
//   * onSnapshot users/{uid}   — status, firmId, role, permissions, superAdmin, …;
//   * onSnapshot firms/{firmId} — lifecycle status, subscription, branding, ….
// deriveSessionStatus (sessionModel.js) turns them into one SESSION state.
//
// Transient failures never become REMOVED / SUSPENDED / FIRM_UNAVAILABLE: a
// listener error is retried with backoff and surfaces as ERROR_RETRYABLE; a
// session stuck loading (e.g. offline cold start, cache-only snapshots) becomes
// ERROR_RETRYABLE after STALL_MS rather than rendering cached firm data.
//
// `generation` changes whenever the session leaves ACTIVE or the ACTIVE tenant
// (uid:firmId) changes, so asynchronous work started for an older generation
// can be discarded by the caller.
import { useEffect, useReducer, useRef, useCallback } from 'react';
import { SESSION, deriveSessionStatus, normalizeMemberStatus, profileFirmId, isPermissionDenied } from './sessionModel';

export const STALL_MS = 12000;
export const AUTH_NULL_GRACE_MS = 3000;
const SUSPECT_WINDOW_MS = 60000;
const SUSPECT_LIMIT = 3;
export const retryDelayMs = (attempt) => Math.min(30000, 1000 * 2 ** Math.max(0, attempt));

const emptySlot = { phase: 'pending', data: null, fromCache: true, confirmed: false };

export const initialSessionState = {
  auth: 'loading',
  profile: emptySlot,
  firm: { ...emptySlot, firmId: null },
  registering: false,
  stalled: false,
  revalidating: false,
  everActiveTenant: null,
  suspectStorm: false,
  suspects: [],
  profileEpoch: 0,
  firmEpoch: 0,
  profileAttempts: 0,
  firmAttempts: 0,
  derived: { status: SESSION.AUTH_LOADING },
  generation: 0,
  authTime: { phase: 'pending', value: null }, // Phase B: current ID token auth_time (seconds)
  // A null auth event arrived and is being held for AUTH_NULL_GRACE_MS: the
  // session is signing out (or blipping). Never decide PROFILE_MISSING meanwhile.
  authNullPending: false,
};


function base(state, action) {
  switch (action.type) {
    case 'AUTH': {
      const prevUid = state.auth && state.auth !== 'loading' ? state.auth.uid : null;
      const nextUid = action.user ? action.user.uid : null;
      if (prevUid === nextUid && state.auth !== 'loading') return { ...state, auth: action.user, authNullPending: false };
      return {
        ...initialSessionState,
        registering: state.registering,
        generation: state.generation,
        derived: state.derived,
        profileEpoch: state.profileEpoch + 1,
        firmEpoch: state.firmEpoch + 1,
        auth: action.user || null,
      };
    }
    case 'PROFILE_SNAPSHOT': {
      if (!state.auth || state.auth === 'loading' || state.auth.uid !== action.uid) return state;
      const profile = {
        phase: action.exists ? 'ok' : 'missing',
        data: action.exists ? action.data : null,
        fromCache: action.fromCache,
        confirmed: state.profile.confirmed || !action.fromCache,
      };
      return { ...state, profile, profileAttempts: action.fromCache ? state.profileAttempts : 0 };
    }
    case 'AUTH_TIME':
      if (!state.auth || state.auth === 'loading' || state.auth.uid !== action.uid) return state;
      return { ...state, authTime: action.ok ? { phase: 'ok', value: action.value } : { phase: 'error', value: null } };
    case 'PROFILE_ERROR':
      if (!state.auth || state.auth === 'loading' || state.auth.uid !== action.uid) return state;
      return { ...state, profile: { ...emptySlot, phase: 'error', code: action.code } };
    case 'FIRM_SNAPSHOT': {
      const sameFirm = state.firm.firmId === action.firmId;
      const firm = {
        phase: action.exists ? 'ok' : 'missing',
        data: action.exists ? action.data : null,
        fromCache: action.fromCache,
        confirmed: (sameFirm && state.firm.confirmed) || !action.fromCache,
        firmId: action.firmId,
      };
      return { ...state, firm, firmAttempts: action.fromCache ? state.firmAttempts : 0 };
    }
    case 'FIRM_ERROR':
      return { ...state, firm: { ...emptySlot, firmId: action.firmId, phase: isPermissionDenied(action.code) ? 'denied' : 'error', code: action.code } };
    case 'FIRM_LISTEN':
      // A (new) firm listener is attaching: nothing about this firm is confirmed yet.
      if (state.firm.firmId === action.firmId && state.firm.phase !== 'denied' && state.firm.phase !== 'error') return state;
      return { ...state, firm: { ...emptySlot, firmId: action.firmId } };
    case 'RETRY_PROFILE':
      return { ...state, profileEpoch: state.profileEpoch + 1, profileAttempts: state.profileAttempts + 1, profile: state.profile.phase === 'error' ? emptySlot : { ...state.profile, confirmed: false } };
    case 'RETRY_FIRM':
      return { ...state, firmEpoch: state.firmEpoch + 1, firmAttempts: state.firmAttempts + 1, firm: { ...emptySlot, firmId: state.firm.firmId } };
    case 'SUSPECT': {
      // A firm-scoped data listener was refused: stop treating anything as current
      // and re-confirm profile + firm from the server before ACTIVE again.
      if (state.revalidating) return state;
      const now = action.now;
      const suspects = [...state.suspects.filter((t) => now - t < SUSPECT_WINDOW_MS), now];
      return {
        ...state,
        revalidating: true,
        suspects,
        suspectStorm: suspects.length >= SUSPECT_LIMIT,
        profile: { ...state.profile, confirmed: false },
        firm: { ...emptySlot, firmId: state.firm.firmId },
        profileEpoch: state.profileEpoch + 1,
        firmEpoch: state.firmEpoch + 1,
      };
    }
    case 'REGISTERING':
      return { ...state, registering: !!action.value };
    case 'AUTH_NULL_PENDING':
      return state.authNullPending === !!action.value ? state : { ...state, authNullPending: !!action.value };
    case 'STALLED':
      return { ...state, stalled: !!action.value };
    case 'MANUAL_RETRY':
      return {
        ...state,
        stalled: false,
        suspectStorm: false,
        suspects: [],
        profile: { ...emptySlot },
        firm: { ...emptySlot, firmId: state.firm.firmId },
        profileEpoch: state.profileEpoch + 1,
        firmEpoch: state.firmEpoch + 1,
        profileAttempts: 0,
        firmAttempts: 0,
      };
    default:
      return state;
  }
}

export function sessionReducer(state, action) {
  let next = base(state, action);
  if (next === state) return state;
  // Revalidation ends once both documents have been confirmed by the server again.
  if (next.revalidating && next.profile.confirmed && (next.firm.confirmed || next.profile.phase !== 'ok' || normalizeMemberStatus(next.profile.data) !== 'active' || !profileFirmId(next.profile.data))) {
    next = { ...next, revalidating: false };
  }
  let derived = deriveSessionStatus(next);
  // A stall only matters while still loading; any settled outcome clears it.
  const settled = derived.status !== SESSION.PROFILE_LOADING && derived.status !== SESSION.FIRM_LOADING && !(derived.status === SESSION.ERROR_RETRYABLE && derived.reason === 'STALLED');
  if (settled && next.stalled) { next = { ...next, stalled: false }; derived = deriveSessionStatus(next); }
  const prev = state.derived;
  const wasActive = prev.status === SESSION.ACTIVE;
  const isActive = derived.status === SESSION.ACTIVE;
  const tenantChanged = wasActive && isActive && prev.tenant !== derived.tenant;
  const generation = (wasActive !== isActive || tenantChanged) ? state.generation + 1 : state.generation;
  const everActiveTenant = isActive ? derived.tenant : next.everActiveTenant;
  return { ...next, derived, generation, everActiveTenant };
}

// deps: { onAuthChange(cb) → unsub, subscribeDoc(path, onNext, onError) → unsub,
//         isRegistrationInProgress(), onRegistrationChange(cb) → unsub, now() }
export default function useSessionGate(deps) {
  const [state, dispatch] = useReducer(sessionReducer, initialSessionState);
  const depsRef = useRef(deps);
  depsRef.current = deps;
  const graceRef = useRef(null);
  const wasAuthedRef = useRef(false);
  const skipGraceRef = useRef(false);

  // Auth state (with the existing 3 s grace for transient null events while signed in).
  useEffect(() => {
    const d = depsRef.current;
    dispatch({ type: 'REGISTERING', value: d.isRegistrationInProgress() });
    const unReg = d.onRegistrationChange((v) => dispatch({ type: 'REGISTERING', value: v }));
    const unAuth = d.onAuthChange((fbUser) => {
      if (graceRef.current) { clearTimeout(graceRef.current); graceRef.current = null; }
      if (fbUser) { wasAuthedRef.current = true; dispatch({ type: 'AUTH', user: fbUser }); return; }
      if (wasAuthedRef.current && !skipGraceRef.current) {
        dispatch({ type: 'AUTH_NULL_PENDING', value: true });
        graceRef.current = setTimeout(() => { graceRef.current = null; wasAuthedRef.current = false; dispatch({ type: 'AUTH', user: null }); }, AUTH_NULL_GRACE_MS);
      } else {
        wasAuthedRef.current = false; skipGraceRef.current = false;
        dispatch({ type: 'AUTH', user: null });
      }
    });
    return () => { unAuth(); unReg(); if (graceRef.current) clearTimeout(graceRef.current); };
  }, []);

  const uid = state.auth && state.auth !== 'loading' ? state.auth.uid : null;

  // Phase B: the signed-in token's auth_time (sign-in time; unchanged by token
  // refresh), read once per signed-in user from the local ID token.
  useEffect(() => {
    if (!uid || !depsRef.current.getAuthTime) return undefined;
    let cancelled = false;
    const user = state.auth;
    Promise.resolve(depsRef.current.getAuthTime(user))
      .then((value) => { if (!cancelled) dispatch({ type: 'AUTH_TIME', uid, ok: true, value }); })
      .catch(() => { if (!cancelled) dispatch({ type: 'AUTH_TIME', uid, ok: false }); });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid, state.auth]);

  // Live profile listener.
  useEffect(() => {
    if (!uid) return undefined;
    return depsRef.current.subscribeDoc(`users/${uid}`,
      (snap) => dispatch({ type: 'PROFILE_SNAPSHOT', uid, exists: snap.exists, data: snap.data, fromCache: snap.fromCache }),
      (err) => dispatch({ type: 'PROFILE_ERROR', uid, code: err && err.code }));
  }, [uid, state.profileEpoch]);

  // Live firm listener — only for an active member with a firmId.
  const p = state.profile;
  const firmTarget = uid && p.phase === 'ok' && normalizeMemberStatus(p.data) === 'active' ? profileFirmId(p.data) : null;
  useEffect(() => {
    if (!firmTarget) return undefined;
    dispatch({ type: 'FIRM_LISTEN', firmId: firmTarget });
    return depsRef.current.subscribeDoc(`firms/${firmTarget}`,
      (snap) => dispatch({ type: 'FIRM_SNAPSHOT', firmId: firmTarget, exists: snap.exists, data: snap.data, fromCache: snap.fromCache }),
      (err) => dispatch({ type: 'FIRM_ERROR', firmId: firmTarget, code: err && err.code }));
  }, [firmTarget, state.firmEpoch]);

  // Backoff re-attachment after listener errors (a stopped listener never recovers by itself).
  useEffect(() => {
    if (state.profile.phase !== 'error') return undefined;
    const t = setTimeout(() => dispatch({ type: 'RETRY_PROFILE' }), retryDelayMs(state.profileAttempts));
    return () => clearTimeout(t);
  }, [state.profile.phase, state.profileAttempts]);
  useEffect(() => {
    if (state.firm.phase !== 'error' && state.firm.phase !== 'denied') return undefined;
    // A refused firm read usually means the profile changed: re-confirm it too.
    if (state.firm.phase === 'denied') dispatch({ type: 'RETRY_PROFILE' });
    const t = setTimeout(() => dispatch({ type: 'RETRY_FIRM' }), retryDelayMs(state.firmAttempts));
    return () => clearTimeout(t);
  }, [state.firm.phase, state.firmAttempts]);

  // Stall detection: never wait forever, never fall back to cached firm data.
  const loadingNow = state.derived.status === SESSION.PROFILE_LOADING || state.derived.status === SESSION.FIRM_LOADING;
  useEffect(() => {
    if (!loadingNow || state.registering) return undefined;
    const t = setTimeout(() => dispatch({ type: 'STALLED', value: true }), STALL_MS);
    return () => clearTimeout(t);
  }, [loadingNow, state.registering, state.profileEpoch, state.firmEpoch]);

  const reportAccessLost = useCallback(() => dispatch({ type: 'SUSPECT', now: (depsRef.current.now || Date.now)() }), []);
  const retry = useCallback(() => dispatch({ type: 'MANUAL_RETRY' }), []);
  const skipAuthGrace = useCallback(() => { skipGraceRef.current = true; }, []);

  const derived = state.derived;
  return {
    status: derived.status,
    reason: derived.reason || null,
    registering: !!state.registering,
    tenant: derived.status === SESSION.ACTIVE ? derived.tenant : null,
    generation: state.generation,
    authUser: uid ? state.auth : null,
    profile: state.profile.phase === 'ok' ? state.profile.data : null,
    profileConfirmed: !!state.profile.confirmed,
    firm: state.firm.phase === 'ok' ? state.firm.data : null,
    firmId: state.firm.firmId,
    reportAccessLost,
    retry,
    skipAuthGrace,
  };
}
